import React, { useRef, useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { fetchSensorData, fetchMachines, fetchSensorsByMachine } from '../services/sensorService';
import './ChartPage.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

  const ChartPage = () => {
  const getToday = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };
  const chartRef = useRef(null);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [machines, setMachines] = useState([]);
  const [tempStartDate, setTempStartDate] = useState(getToday());
  const [tempEndDate, setTempEndDate] = useState(getToday());
  const [filters, setFilters] = useState({
    machine: '',
    startDate: getToday(),
    endDate: getToday()
  });
  const [excludeOutliers, setExcludeOutliers] = useState(true); // 항상 이상치 제거 활성화

  // IQR 방법을 사용한 이상치 제거 함수
  const removeOutliersIQR = (points) => {
    if (!points || points.length < 4) return points || [];

    // 유효한 y값만 추출
    const values = points.map(p => p.y).filter(v => Number.isFinite(v)).sort((a, b) => a - b);

    if (values.length < 4) return points;

    // Q1, Q3 계산 (25분위수, 75분위수)
    const q1Index = Math.floor(values.length * 0.25);
    const q3Index = Math.floor(values.length * 0.75);

    const q1 = values[q1Index];
    const q3 = values[q3Index];

    // IQR 계산
    const iqr = q3 - q1;

    // 이상치 경계 계산 (1.5 * IQR)
    const lowerBound = q1 - 1.5 * iqr;
    const upperBound = q3 + 1.5 * iqr;

    // 이상치 제거
    return points.filter(p =>
      !Number.isFinite(p.y) ||
      (p.y >= lowerBound && p.y <= upperBound)
    );
  };

  const titleRef = useRef(document.title);
  const makeBaseName = () => {
    const m = filters.machine || 'ALL';
    const range = `${filters.startDate}~${filters.endDate}`;
    const d = new Date();
    const ts = `${d.getFullYear()}${String(d.getMonth()+1).padStart(2,'0')}${String(d.getDate()).padStart(2,'0')}_${String(d.getHours()).padStart(2,'0')}${String(d.getMinutes()).padStart(2,'0')}`;
    return `${m}_${range}_${ts}`;
  };

  const loadChartData = async () => {
    if (!filters.machine) {
      setChartData(null);
      return;
    }
    
    setLoading(true);
    try {
      // 1. 선택된 머신의 모든 센서 목록 가져오기 (DB에서)
      const allSensors = await fetchSensorsByMachine(filters.machine);
      
      // 2. 실제 데이터 가져오기
      const data = await fetchSensorData(
        filters.startDate,
        filters.endDate,
        'all',
        filters.machine,
        '/data'
      );

      // 3. 센서별로 데이터 그룹화
      const sensorGroups = {};
      
      // 먼저 실제 데이터가 있는 센서로 그룹화
      data.forEach(item => {
        const seriesKey = item.machine || item.topic;
        if (!seriesKey) return;
        if (!sensorGroups[seriesKey]) {
          sensorGroups[seriesKey] = [];
        }
        sensorGroups[seriesKey].push({
          x: new Date(item.created_at),
          y: item.temp
        });
      });
      
      // DB의 모든 센서를 추가 (데이터가 없는 센서는 빈 배열로)
      allSensors.forEach(sensor => {
        // sensor_name이 이미 전체 이름이므로 그대로 사용
        if (!sensorGroups[sensor.sensor_name]) {
          sensorGroups[sensor.sensor_name] = [];
        }
      });

      // 시간순 정렬
      Object.keys(sensorGroups).forEach(sensor => {
        sensorGroups[sensor].sort((a, b) => a.x - b.x);
      });

      // Chart.js 데이터 형식으로 변환
      const colors = [
        '#e74c3c', // 빨강 - 주요 데이터용
        '#3498db', // 파랑 - 보조 데이터용
        '#27ae60', // 진한 녹색 - 성공/정상 데이터용
        '#f39c12', // 주황 - 경고 데이터용
        '#9b59b6', // 보라 - 특수 데이터용
        '#95a5a6', // 연한 회색 - 대안 데이터용
        '#34495e', // 남색 - 강조용
        '#e91e63', // 분홍 - 중립 데이터용
        '#f1c40f', // 노랑 - 하이라이트용
        '#8bc34a'  // 연두색 - 일반 데이터용
      ];

      const datasets = Object.keys(sensorGroups).map((sensor, index) => {
        const rawPoints = sensorGroups[sensor];
        const hasData = rawPoints.length > 0;
        
        // 데이터가 있으면 이상치 제거, 없으면 빈 배열
        const filteredPoints = hasData ? removeOutliersIQR(rawPoints) : [];
        
        // 데이터가 없으면 흰색, 있으면 정상 색상
        const color = hasData ? colors[index % colors.length] : '#ffffff';

        return {
          label: sensor,
          data: filteredPoints,
          borderColor: color,
          backgroundColor: color + '20',
          tension: 0.1,
          pointRadius: 2,
          pointHoverRadius: 4
        };
      });

      setChartData({
        datasets
      });

      // 차트 로드 후 높이 재조정
      setTimeout(() => {
        if (window.parent !== window) {
          const bodyHeight = document.body.scrollHeight;
          const documentHeight = document.documentElement.scrollHeight;
          const offsetHeight = document.body.offsetHeight;
          const height = Math.max(bodyHeight, documentHeight, offsetHeight);

          window.parent.postMessage({ type: 'resize', height }, '*');
        }
      }, 200);

    } catch (error) {
      // 차트 데이터 로드 오류 처리
    } finally {
      setLoading(false);
    }
  };

  const [isPrinting, setIsPrinting] = useState(false);

  useEffect(() => {
    const handleBeforePrint = () => {
      setIsPrinting(true);
      // 인쇄 직전 업데이트(애니메이션 없이)
      if (chartRef.current) {
        chartRef.current.options.animation = false;
        chartRef.current.resize();
        chartRef.current.update(0);
      }
    };
    const handleAfterPrint = () => {
      setIsPrinting(false);
      if (chartRef.current) {
        chartRef.current.update(0);
      }
    };
    window.addEventListener('beforeprint', handleBeforePrint);
    window.addEventListener('afterprint', handleAfterPrint);
    return () => {
      window.removeEventListener('beforeprint', handleBeforePrint);
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, []);

  // 기존 beforeprint/afterprint 훅 안을 이렇게 보강
  useEffect(() => {
    const handleBeforePrint = () => {
      setIsPrinting(true);
      if (chartRef.current) {
        chartRef.current.options.animation = false;
        chartRef.current.resize();
        chartRef.current.update(0);
      }
      titleRef.current = document.title;              // 원래 제목 저장
      document.title = makeBaseName();                // ★ 인쇄용 파일명
    };
    const handleAfterPrint = () => {
      setIsPrinting(false);
      if (chartRef.current) chartRef.current.update(0);
      document.title = titleRef.current;              // ★ 원래 제목 복구
    };
    window.addEventListener('beforeprint', handleBeforePrint);
    window.addEventListener('afterprint', handleAfterPrint);
    return () => {
      window.removeEventListener('beforeprint', handleBeforePrint);
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [filters]); // 필터 변경 시 새 이름 반영

  useEffect(() => {
    const loadMachines = async () => {
      try {
        const machineData = await fetchMachines();
        setMachines(machineData);
      } catch (error) {
        // 설비 목록 로드 오류 처리
      }
    };
    loadMachines();

    // iframe 높이 자동 조정
    const adjustIframeHeight = () => {
      if (window.parent !== window) {
        // 실제 콘텐츠 높이 측정 (스크롤바 고려)
        const bodyHeight = document.body.scrollHeight;
        const documentHeight = document.documentElement.scrollHeight;
        const offsetHeight = document.body.offsetHeight;
        
        // 가장 정확한 높이 사용
        const height = Math.max(bodyHeight, documentHeight, offsetHeight);

        window.parent.postMessage({ type: 'resize', height }, '*');
      }
    };

    // 페이지 로드 후 높이 조정 (여러 번 시도)
    setTimeout(adjustIframeHeight, 100);
    setTimeout(adjustIframeHeight, 500);
    setTimeout(adjustIframeHeight, 1000);
    
    // 데이터 로드 후에도 높이 조정
    const timer = setTimeout(adjustIframeHeight, 2000);
    
    return () => clearTimeout(timer);
  }, [chartData]);

  useEffect(() => {
    loadChartData();
  }, [filters, excludeOutliers]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { bottom: 20 } },  // x축 라벨 여유
    plugins: {
      legend: {
        position: 'top',
        labels: {
          // 범례 항목을 알파벳 순으로 정렬
          generateLabels: (chart) => {
            const originalLabels = ChartJS.defaults.plugins.legend.labels.generateLabels(chart);

            // 라벨을 알파벳 순으로 정렬
            return originalLabels.sort((a, b) => a.text.localeCompare(b.text));
          },
          boxWidth: 10,  // 색상 사각형 크기
          padding: 10,   // 항목 간 간격
          usePointStyle: true,  // 점 스타일 사용 (라인 대신 점 사용)
        }
      },
      title: {
        display: true,
        text: filters.machine
          ? `설비: ${filters.machine} / ${filters.startDate} ~ ${filters.endDate}`
          : `${filters.startDate} ~ ${filters.endDate}`,
        font: { size: 16, weight: 'bold' }
      }
    },
    scales: {
      x: {
        type: 'time',
        min: filters.startDate,
        time: {
          unit: 'day',
          tooltipFormat: 'yyyy-MM-dd HH:mm',
          displayFormats: {
            day: 'yyyy-MM-dd'
          }
        },
        ticks: {
          autoSkip: false,
          source: 'auto',
          font: { size: 11 },
          maxRotation: 0,
          minRotation: 0
        },
        title: { display: true, text: '날짜' }
      },
      y: {
        title: { display: true, text: '온도 (°C)' },
        beginAtZero: false,
        ticks: {
          stepSize: 100  // 100도씨 단위로 표시
        },
        afterDataLimits: (scale) => {
          const pad = (scale.max - scale.min) * 0.1;
          scale.min = scale.min - pad;
          scale.max = scale.max + pad;
        }
      }
    },
    interaction: { intersect: false, mode: 'index' }
  };


  return (
    <div className="chart-page" style={{ height: 'auto', overflow: 'hidden' }}>
      <div className="chart-controls">
        <div className="control-group">
          <label>설비:</label>
          <select
            value={filters.machine}
            onChange={(e) => setFilters({...filters, machine: e.target.value})}
          >
            <option value="">선택하세요</option>
            {machines.map(machine => (
              <option key={machine.machine_id} value={machine.machine_name}>
                {machine.machine_name}
              </option>
            ))}
          </select>
        </div>
        <div className="control-group">
          <label>시작일:</label>
          <input
            type="date"
            value={tempStartDate}
            onChange={(e) => setTempStartDate(e.target.value)}
          />
        </div>
        <div className="control-group">
          <label>종료일:</label>
          <input
            type="date"
            value={tempEndDate}
            onChange={(e) => setTempEndDate(e.target.value)}
          />
        </div>  
        <button
          onClick={() => {
            setFilters({
              ...filters,
              startDate: tempStartDate,
              endDate: tempEndDate
            });
            loadChartData(); // 여기서 새 filters로 데이터 조회
          }}
          disabled={loading}
        >
          {loading ? '로딩중...' : '조회'}
        </button>
        <button onClick={() => window.print()} disabled={!chartData}>
          인쇄
        </button>
      </div>

      <div className="chart-container">
        {loading && <div className="loading">데이터를 불러오는 중...</div>}
        {!loading && !chartData && (
          <div className="no-data">
            {!filters.machine ? '설비를 선택하세요.' : '조회된 데이터가 없습니다.'}
          </div>
        )}
        {!loading && chartData && (
          <Line ref={chartRef} data={chartData} options={chartOptions} />
        )}
      </div>
    </div>
  );
};

export default ChartPage;